// src/features/admin/components/settings/system-preferences/components/DocumentDefaults.tsx
import React from 'react';
import { DocumentTextIcon, Squares2X2Icon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { usePreferences } from '../PreferencesContext';

const PAGE_SIZES = [
  { value: 'letter', label: 'Letter (8.5" × 11")', width: 8.5, height: 11 },
  { value: 'legal', label: 'Legal (8.5" × 14")', width: 8.5, height: 14 },
  { value: 'a4', label: 'A4 (210mm × 297mm)', width: 8.27, height: 11.69 },
];

const PAGE_ORIENTATIONS = [
  { value: 'portrait', label: 'Portrait', description: 'Vertical (default)' },
  { value: 'landscape', label: 'Landscape', description: 'Horizontal' },
];

const MARGIN_PRESETS = [
  { value: 'normal', label: 'Normal', top: 1, right: 1, bottom: 1, left: 1 },
  { value: 'narrow', label: 'Narrow', top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 },
  { value: 'wide', label: 'Wide', top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
  { value: 'court', label: 'Court-Compliant', top: 1.5, right: 1, bottom: 1, left: 1.5 },
];

const LINE_SPACING = [
  { value: 'single', label: 'Single', multiplier: 1.0 },
  { value: '1.5', label: '1.5 Lines', multiplier: 1.5 },
  { value: 'double', label: 'Double', multiplier: 2.0 },
];

const DocumentDefaults: React.FC = () => {
  const { preferences, updatePreference } = usePreferences();

  if (!preferences) {
    return <div>Loading...</div>;
  }

  // Get current values from preferences, with fallbacks
  const pageSize = preferences.default_page_size || 'letter';
  const orientation = preferences.default_page_orientation || 'portrait';
  const marginPreset = preferences.default_margin_preset || 'normal';
  const lineSpacing = preferences.default_line_spacing || 'double';
  const lineNumbering = preferences.default_line_numbering || false;
  const showPageNumbers = preferences.default_show_page_numbers !== undefined ? preferences.default_show_page_numbers : true;

  return (
    <div className="document-defaults space-y-8">
      {/* Page Size */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <Squares2X2Icon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Default Page Size
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set the standard page size for new documents. This can be changed per document or template.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={pageSize}
            onChange={(e) => updatePreference('default_page_size', e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {PAGE_SIZES.map(size => (
              <option key={size.value} value={size.value}>{size.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Page Orientation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <ArrowsPointingOutIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Default Page Orientation
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose the default orientation for new documents.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <div className="space-y-3">
            {PAGE_ORIENTATIONS.map(orient => (
              <label
                key={orient.value}
                className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  orientation === orient.value
                    ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="orientation"
                  value={orient.value}
                  checked={orientation === orient.value}
                  onChange={(e) => updatePreference('default_page_orientation', e.target.value)}
                  className="h-4 w-4 text-lex-accent focus:ring-lex-accent"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{orient.label}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{orient.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Margins */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <Squares2X2Icon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Default Margins
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set the default margin sizes for new documents. Court-compliant margins follow legal standards.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={marginPreset}
            onChange={(e) => updatePreference('default_margin_preset', e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {MARGIN_PRESETS.map(preset => (
              <option key={preset.value} value={preset.value}>
                {preset.label} - Top: {preset.top}", Right: {preset.right}", Bottom: {preset.bottom}", Left: {preset.left}"
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Line Spacing */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <DocumentTextIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Default Line Spacing
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set the default spacing between lines. Double-spacing is standard for legal documents.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={lineSpacing}
            onChange={(e) => updatePreference('default_line_spacing', e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {LINE_SPACING.map(spacing => (
              <option key={spacing.value} value={spacing.value}>{spacing.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Document Features */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <DocumentTextIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Default Document Features
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enable or disable default features for new documents.
            </p>
          </div>
        </div>

        <div className="ml-9 space-y-4">
          {/* Line Numbering */}
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={lineNumbering}
              onChange={(e) => updatePreference('default_line_numbering', e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Enable Line Numbering
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Automatically add line numbers to legal documents (commonly required for court filings)
              </div>
            </div>
          </label>

          {/* Page Numbers */}
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={showPageNumbers}
              onChange={(e) => updatePreference('default_show_page_numbers', e.target.checked)}
              className="h-5 w-5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100">
                Show Page Numbers
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Display page numbers in document headers or footers
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default DocumentDefaults;
